/**
 * Acceso al `localStorage` que no revienta cuando el navegador lo bloquea.
 *
 * Con las cookies de terceros bloqueadas, en algunos iframes o en navegadores con la
 * configuración de privacidad al máximo, solo leer `window.localStorage` lanza un
 * `SecurityError`. En el portal público eso tumbaba la página entera (el login no
 * aplica allí, pero `autenticado` lee el token al iniciar). Si el storage no está
 * disponible, los valores viven en memoria: la sesión dura lo que dure la pestaña.
 */

/* Respaldo en memoria para cuando el navegador no deja usar el localStorage */
const respaldo = new Map<string, string>();

/**
 * Devuelve el localStorage, o null si el navegador no deja usarlo.
 * @returns {Storage | null} - El localStorage, o null si está bloqueado.
 * @author Juan Corral
 */
function storage(): Storage | null {
  try {
    return window.localStorage ?? null;
  } catch {
    return null;
  }
}

/**
 * Lee un valor del localStorage.
 * @param {string} clave - La clave a leer.
 * @returns {string | null} - El valor guardado, o null si no existe.
 * @author Juan Corral
 */
export function leerAlmacenamiento(clave: string): string | null {
  const almacen = storage();
  if (almacen) {
    try {
      return almacen.getItem(clave);
    } catch {
      // Bloqueado después de abrir la página: se usa el respaldo
    }
  }
  return respaldo.get(clave) ?? null;
}

/**
 * Guarda un valor en el localStorage (o en memoria si el navegador lo bloquea o está lleno).
 * @param {string} clave - La clave a guardar.
 * @param {string} valor - El valor a guardar.
 * @author Juan Corral
 */
export function guardarAlmacenamiento(clave: string, valor: string): void {
  const almacen = storage();
  if (almacen) {
    try {
      almacen.setItem(clave, valor);
      respaldo.delete(clave);
      return;
    } catch {
      // Bloqueado o sin cuota: se usa el respaldo
    }
  }
  respaldo.set(clave, valor);
}

/**
 * Borra un valor del localStorage y de su respaldo en memoria.
 * @param {string} clave - La clave a borrar.
 * @author Juan Corral
 */
export function borrarAlmacenamiento(clave: string): void {
  respaldo.delete(clave);
  try {
    storage()?.removeItem(clave);
  } catch {
    // Nada que borrar si el navegador no deja usar el storage
  }
}
